import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { ImageUploadField } from '../components/ImageUploadField';

export function MerchantBusinessEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Busca via lista do comerciante (inclui pendentes — o endpoint público oculta não aprovados)
        const { data } = await api.get('/merchant/businesses');
        const list = data.data || [];
        const b = list.find(item => String(item.id) === String(id));
        if (!b) {
          setError('Estabelecimento não encontrado ou você não o administra');
          return;
        }
        setForm({
          name: b.name || '',
          // LE-022: endereço estruturado (partes); address é a string canônica exibida
          street: b.street || '',
          street_number: b.street_number || '',
          neighborhood: b.neighborhood || '',
          phone: b.phone || '',
          whatsapp: b.whatsapp || '',
          description: b.description || '',
          logo_url: b.logo_url || '',
          cover_url: b.cover_url || '',
        });
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Erro ao carregar estabelecimento');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const setImage = (key) => (url) => {
    setForm(prev => ({ ...prev, [key]: url }));
    setMessage(null);
  };

  const clearImage = (key) => () => {
    setForm(prev => ({ ...prev, [key]: '' }));
    setMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.patch(`/merchant/businesses/${id}`, {
        name: form.name,
        // LE-022: envia as partes estruturadas (backend recomputa address)
        street: form.street, street_number: form.street_number, neighborhood: form.neighborhood,
        phone: form.phone, whatsapp: form.whatsapp, description: form.description,
        logo_url: form.logo_url || null, cover_url: form.cover_url || null,
      });
      setMessage({ type: 'ok', text: `Dados de "${data.business.name}" salvos.` });
    } catch (err) {
      const apiErr = err.response?.data?.error;
      setMessage({ type: 'err', text: apiErr?.details?.[0]?.message || apiErr?.message || 'Erro ao salvar.' });
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 12, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 6 };

  if (loading) return <Loading message="Carregando..." />;
  if (error) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--nc-text)' }}>{error}</div>
      <button className="nc-btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/comerciante/estabelecimentos')}>
        Voltar aos meus comércios
      </button>
    </div>
  );

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div className="nc-row">
          <button onClick={() => navigate('/comerciante/estabelecimentos')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Editar comércio</h1>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-primary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            Imagens
          </div>
          <ImageUploadField
            label="Logo do comércio"
            hint="JPEG, PNG ou WebP até 5MB. Aparece nos cards e no mapa."
            value={form.logo_url}
            purpose="business_logo"
            entityId={id}
            onUploaded={setImage('logo_url')}
            onClear={clearImage('logo_url')}
          />
          <ImageUploadField
            label="Capa do comércio"
            hint="JPEG, PNG ou WebP até 5MB. Aparece no topo da página do comércio."
            value={form.cover_url}
            purpose="business_cover"
            entityId={id}
            onUploaded={setImage('cover_url')}
            onClear={clearImage('cover_url')}
            accent="linear-gradient(135deg, #1e4d8c 0%, #153a6b 100%)"
          />

          <div style={{ height: 1, background: 'var(--nc-border)', margin: '6px 0 16px' }} />

          <label style={labelStyle}>Nome</label>
          <input type="text" value={form.name} onChange={set('name')} required style={inputStyle} aria-label="Nome" />

          <label style={labelStyle}>Rua / Logradouro</label>
          <input type="text" value={form.street} onChange={set('street')} style={inputStyle} aria-label="Rua / Logradouro" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Número</label>
              <input type="text" value={form.street_number} onChange={set('street_number')} style={inputStyle} aria-label="Número" />
            </div>
            <div>
              <label style={labelStyle}>Bairro</label>
              <input type="text" value={form.neighborhood} onChange={set('neighborhood')} style={inputStyle} aria-label="Bairro" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Telefone</label>
              <input type="tel" value={form.phone} onChange={set('phone')} style={inputStyle} aria-label="Telefone" />
            </div>
            <div>
              <label style={labelStyle}>WhatsApp</label>
              <input type="tel" value={form.whatsapp} onChange={set('whatsapp')} style={inputStyle} aria-label="WhatsApp" />
            </div>
          </div>

          <label style={labelStyle}>Descrição</label>
          <textarea value={form.description} onChange={set('description')} rows={4} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} aria-label="Descrição" />

          <button
            type="submit"
            disabled={saving}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: saving ? 'rgba(13,92,59,0.7)' : '#0d5c3b', color: 'var(--nc-on-primary)',
              fontSize: 14, fontWeight: 600, marginTop: 4
            }}
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </form>
      </div>
    </div>
  );
}
